import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import './AnalyzeModal.css'; // Reuse same styles
import { generateDisplayName } from '../../utils/nameGenerator';
import { getDefaultModelForAgent } from '../../constants/aiModels';
import { DEFAULT_MODEL } from '../../constants/models';
import ModelSelect from './ModelSelect';

const ImplementModal = ({ isOpen, onClose, onImplement, ticketKey, existingNames = [], project = null }) => {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const [name, setName] = useState('');
  const [additionalContext, setAdditionalContext] = useState('');
  const [model, setModel] = useState(DEFAULT_MODEL || 'auto');
  const [isImplementing, setIsImplementing] = useState(false);

  // Load model from project settings when modal opens
  useEffect(() => {
    if (isOpen) {
      if (!name) {
        const generatedName = generateDisplayName('implementation', existingNames);
        setName(generatedName);
      }
      
      // Load AI model from project settings
      if (project?.aiModel) {
        setModel(project.aiModel);
      } else {
        setModel(getDefaultModelForAgent(project?.aiAgent || 'claude'));
      }
    }
  }, [isOpen, existingNames, name, project]);

  const handleImplement = async () => {
    setIsImplementing(true);
    try {
      await onImplement(name, additionalContext, model);
      // Modal will be closed by parent after navigation
    } catch (error) {
      console.error('Error starting implementation:', error);
      setIsImplementing(false);
    }
  };

  const handleClose = () => {
    if (!isImplementing) {
      setName('');
      setAdditionalContext('');
      setModel('auto');
      onClose();
    }
  };

  if (!isOpen) return null;

  const aiAgent = project?.aiAgent || 'claude';
  const hasApiKey = !!project?.secrets?.aiApiKey;
  const keyLabel = aiAgent === 'claude' ? 'Claude API' : 'API';

  return (
    <div className="analyze-modal-overlay" onClick={handleClose}>
      <div className="analyze-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Implement Ticket {ticketKey}</h2>
          <button className="close-btn" onClick={handleClose} disabled={isImplementing}>
            ×
          </button>
        </div>
        
        <div className="modal-content">
          {!hasApiKey && (
            <div className="analyze-api-key-alert">
              <span className="alert-icon">&#9888;</span>
              <div>
                <strong>{keyLabel} key is not configured.</strong>
                {' '}An API key is required to run implementation.{' '}
                <a
                  href={`/projects/${projectId}/settings?section=ai`}
                  onClick={(e) => {
                    e.preventDefault();
                    onClose();
                    navigate(`/projects/${projectId}/settings?section=ai`);
                  }}
                >
                  Go to AI Settings
                </a>
              </div>
            </div>
          )}

          <div className="analyze-input-section">
            <label htmlFor="execution-name">
              ID
            </label>
            <input
              id="execution-name"
              type="text"
              value={name}
              readOnly
              style={{ opacity: 0.7, cursor: 'default' }}
            />
          </div>

          <div className="analyze-input-section" style={{ marginTop: '16px' }}>
            <label htmlFor="additional-context">
              Additional Context (Optional)
            </label>
            <textarea
              id="additional-context"
              value={additionalContext}
              onChange={(e) => setAdditionalContext(e.target.value)}
              placeholder="Add any specific requirements, constraints, or context for the implementation..."
              rows={4}
              disabled={isImplementing}
            />
          </div>

          <div className="analyze-input-section" style={{ marginTop: '16px' }}>
            <ModelSelect
              aiAgent={aiAgent}
              value={model}
              onChange={setModel}
              disabled={isImplementing}
              label={`AI Model ${aiAgent === 'claude' ? '(Claude API)' : ''}`}
            />
          </div>

          <button
            className="analyze-btn"
            onClick={handleImplement}
            disabled={isImplementing || !name.trim() || !hasApiKey}
          >
            {isImplementing ? 'Starting Implementation...' : 'Start Implementation'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImplementModal;
